import { useEffect, useState } from "react";

import { useMediaQuery } from "./use-media-query";

export type MobileVisualViewportAdjust = {
  /** Lift fixed `bottom: 0` content by this many pixels (keyboard + browser chrome). */
  bottomPx: number;
  /** Optional max height so the sheet fits in the visible viewport above the keyboard. */
  maxVisibleHeightPx: number | undefined;
};

/**
 * iOS Safari (and similar) keep `100vh` / layout viewport tall when the virtual
 * keyboard opens; `fixed bottom-0` dialogs stay behind the keyboard. Use the visual
 * viewport to lift bottom-anchored UI and cap height.
 *
 * Only active below the `sm` breakpoint (< 640px). Keyboard geometry comes from
 * `visualViewport` — opening the keyboard usually does not change `innerWidth`, so
 * width-based gating alone cannot detect it; we still read `visualViewport` for the
 * actual offsets.
 */
export function useMobileVisualViewportAdjust(): MobileVisualViewportAdjust {
  const isBelowSm = useMediaQuery("(max-width: 639px)");

  const [state, setState] = useState<MobileVisualViewportAdjust>({
    bottomPx: 0,
    maxVisibleHeightPx: undefined,
  });

  useEffect(() => {
    if (!isBelowSm) {
      setState({ bottomPx: 0, maxVisibleHeightPx: undefined });
      return;
    }

    const vv = window.visualViewport;
    if (!vv) {
      setState({ bottomPx: 0, maxVisibleHeightPx: undefined });
      return;
    }

    const update = () => {
      const bottomPx = Math.max(
        0,
        window.innerHeight - vv.offsetTop - vv.height,
      );

      setState({
        bottomPx,
        maxVisibleHeightPx:
          bottomPx > 0 ? Math.max(160, Math.round(vv.height) - 24) : undefined,
      });
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);

    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, [isBelowSm]);

  return state;
}
